import React, { useState, useEffect } from 'react';
import { Plus, Wifi, Trash2, Edit2, X, Eye, EyeOff, User, CheckCircle } from 'lucide-react';
import { vpnApi } from '../api';

const VpnPage = () => {
  const [poolList, setPoolList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterAssigned, setFilterAssigned] = useState('');
  const [showPoolDialog, setShowPoolDialog] = useState(false);
  const [editingPool, setEditingPool] = useState(null);
  const [showUrl, setShowUrl] = useState({});

  const [poolForm, setPoolForm] = useState({
    account_name: '',
    sub_url: '',
    clash_url: '',
    v2ray_url: '',
    ss_url: '',
    trojan_url: '',
    vless_url: '',
    expired_at: '',
    notes: '',
    status: 'active'
  });

  useEffect(() => {
    loadPool();
  }, [filterAssigned]);

  const loadPool = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAssigned !== '') params.is_assigned = filterAssigned;
      const res = await vpnApi.getPoolList(params);
      if (res.success) setPoolList(res.data);
    } catch (err) {
      console.error('加载链接池失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingPool(null);
    setPoolForm({
      account_name: '', sub_url: '',
      clash_url: '', v2ray_url: '', ss_url: '', trojan_url: '', vless_url: '',
      expired_at: '', notes: '', status: 'active'
    });
    setShowPoolDialog(true);
  };

  const openEdit = (pool) => {
    setEditingPool(pool);
    setPoolForm({
      account_name: pool.account_name || '',
      sub_url: pool.sub_url || '',
      clash_url: pool.clash_url || '',
      v2ray_url: pool.v2ray_url || '',
      ss_url: pool.ss_url || '',
      trojan_url: pool.trojan_url || '',
      vless_url: pool.vless_url || '',
      expired_at: pool.expired_at ? pool.expired_at.slice(0, 10) : '',
      notes: pool.notes || '',
      status: pool.status || 'active'
    });
    setShowPoolDialog(true);
  };

  const handleSave = async () => {
    if (!poolForm.account_name || !poolForm.sub_url) {
      alert('账号名称和通用订阅链接不能为空');
      return;
    }
    try {
      let res;
      if (editingPool) {
        res = await vpnApi.updatePool(editingPool.id, poolForm);
      } else {
        res = await vpnApi.createPool(poolForm);
      }
      if (res.success) {
        alert(editingPool ? '更新成功' : '添加成功');
        setShowPoolDialog(false);
        loadPool();
      } else {
        alert(res.message || '操作失败');
      }
    } catch (err) {
      alert(err.message || '操作失败');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定删除该账号？')) return;
    try {
      const res = await vpnApi.deletePool(id);
      if (res.success) { alert('删除成功'); loadPool(); }
      else alert(res.message || '删除失败');
    } catch (err) {
      alert(err.response?.data?.message || '删除失败');
    }
  };

  const handleUnassign = async (pool_id) => {
    if (!window.confirm('确定取消该账号的分配？取消后客户将无法使用该订阅。')) return;
    try {
      const res = await vpnApi.unassignFromCustomer(pool_id);
      if (res.success) { alert('已取消分配'); loadPool(); }
      else alert(res.message || '操作失败');
    } catch (err) {
      alert('操作失败');
    }
  };

  const toggleShowUrl = (id) => setShowUrl(prev => ({ ...prev, [id]: !prev[id] }));

  const totalCount = poolList.length;
  const assignedCount = poolList.filter(p => p.is_assigned).length;
  const availableCount = poolList.filter(p => !p.is_assigned && p.status === 'active').length;

  const UrlField = ({ label, value, id, field }) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input type="text" value={value}
        onChange={e => setPoolForm({ ...poolForm, [field]: e.target.value })}
        placeholder="https://..."
        className="w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-blue-500" />
    </div>
  );

  return (
    <div className="p-6 space-y-6 h-full overflow-y-auto">
      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">VPN 管理</h2>
          <p className="text-xs text-gray-600 mt-1">管理机场账号订阅链接和客户分配</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700">
          <Plus className="w-4 h-4" /><span>添加账号</span>
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-500 mb-1">账号总数</p>
          <p className="text-2xl font-bold text-gray-800">{totalCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-500 mb-1">已分配</p>
          <p className="text-2xl font-bold text-blue-600">{assignedCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-500 mb-1">可分配</p>
          <p className="text-2xl font-bold text-green-600">{availableCount}</p>
        </div>
      </div>

      {/* 筛选 */}
      <div className="flex items-center gap-3">
        <select value={filterAssigned} onChange={e => setFilterAssigned(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
          <option value="">全部账号</option>
          <option value="0">未分配</option>
          <option value="1">已分配</option>
        </select>
        <p className="text-xs text-gray-500">共 {poolList.length} 条</p>
      </div>

      {/* 列表 */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : poolList.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Wifi className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>暂无账号，点击右上角添加</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">账号名称</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">通用订阅链接</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">支持协议</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">到期时间</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">分配状态</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {poolList.map(pool => (
                <tr key={pool.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-800">{pool.account_name}</p>
                    {pool.notes && <p className="text-xs text-gray-400">{pool.notes}</p>}
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500 truncate max-w-[160px]">
                        {showUrl[pool.id] ? pool.sub_url : '••••••••••••••••'}
                      </span>
                      <button onClick={() => toggleShowUrl(pool.id)}
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                        {showUrl[pool.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {pool.clash_url && <span className="px-1.5 py-0.5 text-xs rounded bg-green-100 text-green-700">Clash</span>}
                      {pool.v2ray_url && <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700">V2ray</span>}
                      {pool.ss_url && <span className="px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700">SS</span>}
                      {pool.trojan_url && <span className="px-1.5 py-0.5 text-xs rounded bg-orange-100 text-orange-700">Trojan</span>}
                      {pool.vless_url && <span className="px-1.5 py-0.5 text-xs rounded bg-cyan-100 text-cyan-700">Vless</span>}
                      {!pool.clash_url && !pool.v2ray_url && !pool.ss_url && !pool.trojan_url && !pool.vless_url && (
                        <span className="text-xs text-gray-400">仅通用链接</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {pool.expired_at
                      ? new Date(pool.expired_ts * 1000).toLocaleDateString()
                      : <span className="text-gray-300">未设置</span>}
                  </td>
                  <td className="px-4 py-3">
                    {pool.is_assigned ? (
                      <div>
                        <div className="flex items-center gap-1 text-blue-600">
                          <User className="w-3.5 h-3.5" />
                          <span className="text-xs font-medium">
                            {pool.customer_name || pool.customer_username || `客户#${pool.assigned_customer_id}`}
                          </span>
                        </div>
                        <button onClick={() => handleUnassign(pool.id)}
                          className="text-xs text-red-500 hover:text-red-700 mt-0.5">
                          取消分配
                        </button>
                      </div>
                    ) : (
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                        pool.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {pool.status === 'active' ? '可分配' : '已停用'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => openEdit(pool)}
                        className="text-blue-600 hover:text-blue-800">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {!pool.is_assigned && (
                        <button onClick={() => handleDelete(pool.id)}
                          className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 添加/编辑弹窗 */}
      {showPoolDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-bold text-gray-800">
                {editingPool ? '编辑账号' : '添加账号'}
              </h3>
              <button onClick={() => setShowPoolDialog(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">账号名称 *</label>
                <input type="text" value={poolForm.account_name}
                  onChange={e => setPoolForm({ ...poolForm, account_name: e.target.value })}
                  placeholder="对应机场面板的用户名"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">通用订阅链接 *</label>
                <textarea value={poolForm.sub_url}
                  onChange={e => setPoolForm({ ...poolForm, sub_url: e.target.value })}
                  placeholder="https://... （支持所有客户端）"
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>

              {/* 专用链接 */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-medium text-gray-700">专用链接（选填）</p>
                <UrlField label="Clash 专用" value={poolForm.clash_url} field="clash_url" />
                <UrlField label="V2ray 专用" value={poolForm.v2ray_url} field="v2ray_url" />
                <UrlField label="SS 专用" value={poolForm.ss_url} field="ss_url" />
                <UrlField label="Trojan 专用" value={poolForm.trojan_url} field="trojan_url" />
                <UrlField label="Vless 专用" value={poolForm.vless_url} field="vless_url" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">到期时间</label>
                  <input type="date" value={poolForm.expired_at}
                    onChange={e => setPoolForm({ ...poolForm, expired_at: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">状态</label>
                  <select value={poolForm.status}
                    onChange={e => setPoolForm({ ...poolForm, status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                    <option value="active">启用</option>
                    <option value="disabled">停用</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">备注</label>
                <input type="text" value={poolForm.notes}
                  onChange={e => setPoolForm({ ...poolForm, notes: e.target.value })}
                  placeholder="例：香港节点、高速线路..."
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="p-5 border-t flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowPoolDialog(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                取消
              </button>
              <button onClick={handleSave}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                {editingPool ? '保存' : '添加'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VpnPage;